import { useCallback, useEffect, useRef, useState } from 'react';
import { Camera, Check, Diamond, Keyboard, Loader2, RefreshCw, ScanLine, X } from 'lucide-react';
import type { ScanMatch, ScanMatchCandidate } from '@siliconx/shared';
import { cn } from '@siliconx/shared/ui/utils';
import { Button } from '@siliconx/shared/ui/Button';
import {
  createScan,
  describeScanBlock,
  downscalePhoto,
  ocrScan,
  requestUploadUrl,
  ScanRequestError,
  scanIdFromKey,
  selectScanSku,
  uploadScanPhoto,
  type ScanBlock,
} from '../../lib/scan';

/**
 * Full-screen (mobile) / dialog (desktop) hardware-label capture (RUF-500).
 *
 * Capture chain per docs/research/scan-intake/browser-barcode-scanning.md:
 * live camera (barcode-detector ponyfill, lazy chunk) → "Take a photo"
 * (downscale → presigned PUT → OCR) → manual entry.
 *
 * Result copy NEVER shows catalog ids or the word "SKU" — only
 * manufacturer / model / serial (scan.ts DTO contract).
 */

export interface AcceptedScan {
  scanId: string;
  manufacturer: string | null;
  model: string | null;
  serial: string | null;
  skuId: string | null;
  /** True when the model is a new-to-catalog draft pending ops review. */
  draft: boolean;
}

type Phase =
  | 'idle' // camera unavailable/stopped — photo + manual fallbacks
  | 'requesting' // getUserMedia in flight — before the OS permission prompt resolves
  | 'scanning' // live camera running, decode loop active
  | 'decoding' // photo being downscaled/uploaded
  | 'looking-up' // scan created / OCR running, waiting on the match
  | 'result'
  | 'error';

const DRAFT_NOTE = "We don't recognize this model yet — it will be added to the catalog after review.";
const DECODE_INTERVAL_MS = 250;

export function ScanCaptureOverlay({
  onAccept,
  onClose,
  onBlocked,
  block,
}: {
  onAccept: (scan: AcceptedScan) => void;
  onClose: () => void;
  /** 429/503 reason surfaced up so the form can disable its scan CTA. */
  onBlocked: (block: ScanBlock) => void;
  /** Currently known block (e.g. photo path already out of quota). */
  block: ScanBlock | null;
}) {
  const [phase, setPhase] = useState<Phase>('idle');
  const [hint, setHint] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [result, setResult] = useState<{ scanId: string; match: ScanMatch } | null>(null);
  const [manualValue, setManualValue] = useState('');
  const [selectPending, setSelectPending] = useState(false);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const closedRef = useRef(false);

  const photoBlocked = block !== null; // scope 'all' or 'photo' both stop OCR
  const fullyBlocked = block?.scope === 'all';

  const stopCamera = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  /** Route an API failure: quota/feature blocks bubble up, the rest retry. */
  const handleFailure = useCallback(
    (err: unknown) => {
      if (closedRef.current) return;
      const scanBlock =
        err instanceof ScanRequestError ? describeScanBlock(err.code) : null;
      if (scanBlock) {
        onBlocked(scanBlock);
        // scope 'all' renders the blocked panel via the `block` prop; a
        // photo-only block returns to capture with a hint.
        if (scanBlock.scope === 'photo') {
          setHint(scanBlock.message);
          setPhase('idle');
        }
        return;
      }
      setErrorMsg(err instanceof Error ? err.message : 'Something went wrong.');
      setPhase('error');
    },
    [onBlocked],
  );

  const lookUp = useCallback(
    async (decodedValue: string, captureMethod: 'barcode' | 'manual') => {
      stopCamera();
      setPhase('looking-up');
      try {
        const res = await createScan(decodedValue, captureMethod);
        if (closedRef.current) return;
        setResult(res);
        setPhase('result');
      } catch (err) {
        handleFailure(err);
      }
    },
    [handleFailure, stopCamera],
  );

  const startCamera = useCallback(async () => {
    setHint(null);
    setErrorMsg(null);
    setResult(null);
    if (!navigator.mediaDevices?.getUserMedia) {
      setPhase('idle');
      setHint('Camera not available on this device — take a photo or type the label.');
      return;
    }
    // A one-line primer before the OS permission prompt appears, so a
    // "Camera unavailable" result never lands as the very first thing a
    // user sees after tapping "Scan with camera" (SIL critique).
    setPhase('requesting');
    try {
      // Lazy chunk: ponyfill (+ wasm) loads only when scanning actually starts.
      const detectorPromise = import('./barcode').then((m) => m.createDetector());
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 } },
        audio: false,
      });
      if (closedRef.current) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => undefined);
      }
      const detector = await detectorPromise;
      setPhase('scanning');
      let busy = false;
      timerRef.current = setInterval(async () => {
        const video = videoRef.current;
        if (busy || !video || video.readyState < 2 || closedRef.current) return;
        busy = true;
        try {
          const codes = await detector.detect(video);
          const raw = codes[0]?.rawValue?.trim();
          if (raw) void lookUp(raw, 'barcode');
        } catch {
          // Per-frame decode failures are routine — keep scanning.
        } finally {
          busy = false;
        }
      }, DECODE_INTERVAL_MS);
    } catch (err) {
      // Permission denied, no camera, or detector failed to load — tell the
      // user which, since the fix differs (browser settings vs. just typing
      // it in).
      stopCamera();
      if (closedRef.current) return;
      setPhase('idle');
      const name = err instanceof DOMException ? err.name : null;
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        setHint("Camera access was denied — allow it in your browser's site settings, or take a photo or type the label instead.");
      } else if (name === 'NotFoundError' || name === 'OverconstrainedError') {
        setHint('No camera found on this device — take a photo of the label or type it instead.');
      } else {
        setHint('Camera unavailable — take a photo of the label or type it instead.');
      }
    }
  }, [lookUp, stopCamera]);

  // Open → straight into live scanning (unless scanning is blocked).
  useEffect(() => {
    closedRef.current = false;
    if (!fullyBlocked) void startCamera();
    return () => {
      closedRef.current = true;
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount/unmount only
  }, []);

  async function handlePhoto(file: File) {
    stopCamera();
    setPhase('decoding');
    try {
      const blob = await downscalePhoto(file);
      const { url, key } = await requestUploadUrl();
      const scanId = scanIdFromKey(key);
      if (!scanId) throw new Error('unexpected upload key from server');
      await uploadScanPhoto(url, blob);
      if (closedRef.current) return;
      setPhase('looking-up');
      const res = await ocrScan(scanId, key);
      if (closedRef.current) return;
      if (res.needsManualEntry) {
        setHint('We could not read that label — try another photo or type it below.');
        setPhase('idle');
        return;
      }
      setResult({ scanId: res.scanId, match: res.match });
      setPhase('result');
    } catch (err) {
      handleFailure(err);
    }
  }

  function handleManualSubmit(e: React.FormEvent) {
    e.preventDefault();
    const v = manualValue.trim();
    if (v) void lookUp(v, 'manual');
  }

  function accept(scan: AcceptedScan) {
    onAccept(scan);
  }

  async function pickCandidate(scanId: string, match: ScanMatch, c: ScanMatchCandidate) {
    setSelectPending(true);
    try {
      await selectScanSku(scanId, { skuId: c.skuId });
      accept({
        scanId,
        manufacturer: c.manufacturer ?? match.manufacturer,
        model: c.modelName,
        serial: match.serial,
        skuId: c.skuId,
        draft: false,
      });
    } catch (err) {
      handleFailure(err);
    } finally {
      setSelectPending(false);
    }
  }

  async function pickNone(scanId: string, match: ScanMatch) {
    setSelectPending(true);
    try {
      await selectScanSku(scanId, { none: true });
      accept({
        scanId,
        manufacturer: match.manufacturer,
        model: match.model,
        serial: match.serial,
        skuId: null,
        draft: true,
      });
    } catch (err) {
      if (err instanceof ScanRequestError && err.code === 'draft_unavailable') {
        setHint('That label needs manual entry — type the model below.');
        setPhase('idle');
        return;
      }
      handleFailure(err);
    } finally {
      setSelectPending(false);
    }
  }

  const manualEntry = (
    <form onSubmit={handleManualSubmit} className="flex gap-2">
      <input
        className="h-10 w-full rounded-md border border-border bg-card px-3 font-num text-sm text-foreground outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500"
        value={manualValue}
        onChange={(e) => setManualValue(e.target.value)}
        placeholder="Part number or serial from the label"
        aria-label="Enter label value manually"
      />
      <Button className="flex-none font-semibold gap-1.5 h-10 inline-flex items-center px-3"
        type="submit"
        disabled={!manualValue.trim()}
      >
        Look up
      </Button>
    </form>
  );

  const photoInput = !photoBlocked && (
    <label className="inline-flex h-10 w-full cursor-pointer items-center justify-center gap-2 rounded-md border border-border text-sm font-medium text-foreground hover:bg-muted">
      <Camera className="h-4 w-4" /> Take a photo
      <input
        type="file"
        accept="image/*"
        capture="environment"
        className="sr-only"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (f) void handlePhoto(f);
        }}
      />
    </label>
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-center bg-black/40 sm:items-center sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Scan hardware label"
    >
      <div className="flex w-full flex-col overflow-y-auto bg-card sm:max-w-md sm:rounded-xl sm:border sm:border-border sm:shadow-xl">
        {/* Header */}
        <div className="flex flex-none items-center justify-between border-b border-border px-4 py-3">
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-foreground">
            <ScanLine className="h-4 w-4 text-brand-600" /> Scan hardware label
          </span>
          <Button variant="icon" className="rounded"
            aria-label="Close scanner"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex flex-1 flex-col">
          {fullyBlocked ? (
            <div className="flex flex-col gap-3 p-5">
              <p className="text-sm text-foreground">{block?.message}</p>
              <button
                type="button"
                onClick={onClose}
                className="inline-flex h-10 items-center justify-center rounded-md border border-border text-sm font-medium text-foreground hover:bg-muted"
              >
                Back to the form
              </button>
            </div>
          ) : phase === 'requesting' ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 p-10 text-center">
              <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
              <p className="text-sm text-muted-foreground">
                Asking your browser for camera access — allow it to scan the label.
              </p>
            </div>
          ) : phase === 'scanning' ? (
            <>
              <div className="relative min-h-64 flex-1 bg-zinc-950 sm:aspect-[4/3] sm:flex-none">
                <video
                  ref={videoRef}
                  playsInline
                  muted
                  className="absolute inset-0 h-full w-full object-cover"
                />
                {/* Scan reticle */}
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <div className="relative h-2/5 w-4/5 rounded-lg border-2 border-white/80">
                    <span className="absolute inset-x-3 top-1/2 h-0.5 animate-pulse rounded bg-brand-400/90" />
                  </div>
                </div>
                <p className="absolute inset-x-0 bottom-3 text-center text-xs font-medium text-white/90">
                  Align the barcode on the hardware label within the frame
                </p>
              </div>
              <div className="flex flex-none flex-col gap-2.5 p-4">
                {photoInput}
                {manualEntry}
              </div>
            </>
          ) : phase === 'decoding' || phase === 'looking-up' ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 p-10">
              <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
              <p className="text-sm text-muted-foreground">
                {phase === 'decoding' ? 'Reading the label…' : 'Matching against the catalog…'}
              </p>
            </div>
          ) : phase === 'result' && result ? (
            <ScanResultCard
              scanId={result.scanId}
              match={result.match}
              pending={selectPending}
              onUseExact={(m) =>
                accept({
                  scanId: result.scanId,
                  manufacturer: m.manufacturer,
                  model: m.model,
                  serial: m.serial,
                  skuId: m.tier === 'exact' ? m.skuId : null,
                  draft: false,
                })
              }
              onPickCandidate={(c) => void pickCandidate(result.scanId, result.match, c)}
              onPickNone={() => void pickNone(result.scanId, result.match)}
              onRescan={() => void startCamera()}
            />
          ) : phase === 'error' ? (
            <div className="flex flex-col gap-3 p-5">
              <p className="text-sm text-red-600">{errorMsg ?? 'Something went wrong.'}</p>
              <button
                type="button"
                onClick={() => void startCamera()}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-border text-sm font-medium text-foreground hover:bg-muted"
              >
                <RefreshCw className="h-4 w-4" /> Try again
              </button>
              <div className="border-t border-border pt-3">
                <p className="mb-2 inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                  <Keyboard className="h-3.5 w-3.5" /> Or type the label value
                </p>
                {manualEntry}
              </div>
            </div>
          ) : (
            /* idle — camera off; photo + manual fallbacks */
            <div className="flex flex-col gap-3 p-5">
              {hint && <p className="text-sm text-muted-foreground">{hint}</p>}
              <button
                type="button"
                onClick={() => void startCamera()}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-border text-sm font-medium text-foreground hover:bg-muted"
              >
                <ScanLine className="h-4 w-4" /> Scan with camera
              </button>
              {photoInput}
              <div className="border-t border-border pt-3">
                <p className="mb-2 inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                  <Keyboard className="h-3.5 w-3.5" /> Or type the label value
                </p>
                {manualEntry}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Match result — manufacturer/model/serial ONLY (no catalog ids, never the
 * word "SKU"). Exact → identified badge; fuzzy → top-3 pick-list by model
 * name + "None of these"; none + draftPending → new-model draft note.
 */
function ScanResultCard({
  match,
  pending,
  onUseExact,
  onPickCandidate,
  onPickNone,
  onRescan,
}: {
  scanId: string;
  match: ScanMatch;
  pending: boolean;
  onUseExact: (match: ScanMatch) => void;
  onPickCandidate: (c: ScanMatchCandidate) => void;
  onPickNone: () => void;
  onRescan: () => void;
}) {
  const rows = [
    { label: 'Manufacturer', value: match.manufacturer },
    { label: 'Model', value: match.model },
    { label: 'Serial', value: match.serial },
  ].filter((r) => r.value);

  const rescanButton = (
    <button
      type="button"
      onClick={onRescan}
      className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-border text-sm font-medium text-foreground hover:bg-muted"
    >
      <RefreshCw className="h-4 w-4" /> Scan again
    </button>
  );

  if (match.tier === 'fuzzy') {
    return (
      <div className="flex flex-col gap-3 p-5">
        <p className="text-sm font-semibold text-foreground">Which model is on the label?</p>
        <div className="flex flex-col gap-2">
          {match.candidates.slice(0, 3).map((c) => (
            <button
              type="button"
              key={c.skuId}
              disabled={pending}
              onClick={() => onPickCandidate(c)}
              className="rounded-lg border border-border p-3 text-left hover:border-brand-500 hover:bg-brand-50 disabled:opacity-50"
            >
              <div className="text-sm font-semibold text-foreground">{c.modelName ?? 'Unknown model'}</div>
              {c.manufacturer && <div className="mt-0.5 text-xs text-muted-foreground">{c.manufacturer}</div>}
            </button>
          ))}
          <button
            type="button"
            disabled={pending}
            onClick={onPickNone}
            className="rounded-lg border border-dashed border-border p-3 text-left text-sm font-medium text-muted-foreground hover:bg-muted disabled:opacity-50"
          >
            None of these
          </button>
        </div>
        <p className="text-xs leading-snug text-muted-foreground">
          Picking “None of these” submits it as a new model. {DRAFT_NOTE}
        </p>
        {rescanButton}
      </div>
    );
  }

  const identified = match.tier === 'exact';
  const draftable = match.tier === 'none' && match.draftPending;

  return (
    <div className="flex flex-col gap-3 p-5">
      {identified ? (
        <span className="inline-flex w-fit items-center gap-1 rounded-md bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200">
          <Check className="h-3.5 w-3.5" /> Model identified
        </span>
      ) : draftable ? (
        <span className="inline-flex w-fit items-center gap-1 rounded-md bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-700 ring-1 ring-inset ring-amber-200">
          <Diamond className="h-3 w-3" /> New model — pending review
        </span>
      ) : null}

      {rows.length > 0 && (
        <dl className="divide-y divide-border rounded-lg border border-border">
          {rows.map((r) => (
            <div key={r.label} className="flex items-center justify-between gap-3 px-3 py-2">
              <dt className="text-xs font-medium text-muted-foreground">{r.label}</dt>
              <dd
                className={cn(
                  'text-right text-sm font-semibold text-foreground',
                  r.label === 'Serial' && 'font-num',
                )}
              >
                {r.value}
              </dd>
            </div>
          ))}
        </dl>
      )}

      {draftable && <p className="text-xs leading-snug text-muted-foreground">{DRAFT_NOTE}</p>}

      {identified ? (
        <Button className="font-semibold gap-2 h-10 inline-flex items-center justify-center"
          disabled={pending}
          onClick={() => onUseExact(match)}
        >
          <Check className="h-4 w-4" /> Use this model
        </Button>
      ) : draftable ? (
        <Button className="font-semibold gap-2 h-10 inline-flex items-center justify-center"
          disabled={pending}
          onClick={onPickNone}
        >
          <Check className="h-4 w-4" /> Use it anyway
        </Button>
      ) : (
        <p className="text-sm text-muted-foreground">
          We couldn't identify this label — try scanning again or fill the form manually.
        </p>
      )}
      {rescanButton}
    </div>
  );
}
